import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { FreelancerHolidayRequestResource } from '@/types/resources';
import { api } from '@/utils/api';
import { VacationStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  freelancer_id?: string | number;
  request_date: string;
  start_date: string;
  end_date: string;
  hr_status: VacationStatus;
  team_lead_status: VacationStatus;
  notes?: string;
};

interface Props {
  request?: FreelancerHolidayRequestResource;
  onClose: () => void;
}

const FreelancerVacationRequestModal: FC<Props> = ({ request, onClose }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const methods = useForm<FormDataT>({
    defaultValues: {
      freelancer_id: request?.freelancerId ?? id,
      request_date: request?.requestedAt
        ? DateTime.fromISO(request.requestedAt).toFormat('yyyy-LL-dd')
        : new Date().toISOString().split('T')[0],
      start_date: request?.startDate,
      end_date: request?.endDate,
      hr_status: request?.hrStatus ?? VacationStatus.Pending,
      team_lead_status: request?.teamLeadStatus ?? VacationStatus.Pending,
      notes: request?.notes ?? '',
    },
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (request) {
          await api.put<never, { id: number }>(
            `/v1/payments/freelancers/holidays/requests/${request.id}`,
            data,
          );
        } else {
          await api.post<never, { id: number }>(
            `/v1/payments/freelancers/holidays/requests`,
            data,
          );
        }

        await queryClient.invalidateQueries(['freelancer_vacation_requests'], {
          exact: false,
        });

        await queryClient.invalidateQueries(['freelancer_vacation_balance'], {
          exact: false,
        });

        alerts.success(
          `Vacation request ${request ? 'updated' : 'created'}  successfully`,
        );
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, false, true);
      }
    })(e);
  };

  const vacationStatusesOptions = [
    { label: 'Pending', value: VacationStatus.Pending },
    { label: 'Declined', value: VacationStatus.Declined },
    { label: 'Approved', value: VacationStatus.Approved },
    { label: 'Auto Declined', value: VacationStatus.Auto_Declined },
  ];

  const content = (
    <SidePanel
      heading={`${request ? 'Edit' : 'Create'} Vacation Request`}
      onClose={onClose}
    >
      <SidePanelBody onSubmit={onSubmit} autoComplete="off">
        <SidePanelContent>
          <FormLayout>
            <FormField<FormDataT>
              control={control}
              name="request_date"
              type="text"
              label="Request Date"
            />
            <FormField<FormDataT>
              control={control}
              name="start_date"
              type="datepicker"
              datepickerType="date"
              label="Date From"
              disableDayDimming
            />
            <FormField<FormDataT>
              control={control}
              name="end_date"
              type="datepicker"
              datepickerType="date"
              label="Date To"
              disableDayDimming
            />
            <FormField<FormDataT>
              control={control}
              name="team_lead_status"
              type="select"
              options={vacationStatusesOptions}
              label="Team Lead Status"
            />
            <FormField<FormDataT>
              control={control}
              name="hr_status"
              type="select"
              options={vacationStatusesOptions}
              label="HR Status"
            />
            <FormField<FormDataT>
              control={control}
              name="notes"
              type="textarea"
              label="Comment"
              optional
            />
          </FormLayout>
        </SidePanelContent>
        <SidePanelFooter $actions>
          <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
            Submit
          </Button>
          <Button color="secondary" onClick={onClose} type="button">
            Close
          </Button>
        </SidePanelFooter>
      </SidePanelBody>
    </SidePanel>
  );

  return request ? <Modal>{content}</Modal> : content;
};

export default FreelancerVacationRequestModal;
